// LZ77 implementation with a sliding window

// Encode data using LZ77 algorithm
export function lz77Encode(data, windowSize = 1024, lookaheadBuffer = 32) {
  const encoded = [];
  let position = 0;
  
  while (position < data.length) {
    // Define the search buffer (already encoded data within the window)
    const searchBufferStart = Math.max(0, position - windowSize);
    const searchBuffer = data.slice(searchBufferStart, position);
    
    // Define the lookahead buffer (data to be encoded)
    const remainingLength = Math.min(lookaheadBuffer, data.length - position);
    const lookahead = data.slice(position, position + remainingLength);
    
    let bestMatch = { offset: 0, length: 0, nextChar: lookahead[0] || 0 };
    
    // Find the longest match in the search buffer
    for (let i = 0; i < searchBuffer.length; i++) {
      let matchLength = 0;
      
      // Find the length of the match
      while (
        matchLength < lookahead.length && 
        i + matchLength < searchBuffer.length && 
        searchBuffer[i + matchLength] === lookahead[matchLength]
      ) {
        matchLength++;
      }
      
      // Update best match if this one is better
      if (matchLength > bestMatch.length) {
        bestMatch = {
          offset: searchBuffer.length - i,
          length: matchLength,
          nextChar: lookahead[matchLength] || 0
        };
      }
    }
    
    // Add the best match to the encoded data
    if (bestMatch.length > 2) {
      // Encode as a reference (offset, length, nextChar)
      encoded.push({
        type: 'reference',
        offset: bestMatch.offset,
        length: bestMatch.length,
        nextChar: bestMatch.nextChar
      });
      position += bestMatch.length + 1; // +1 for the nextChar
    } else {
      // Encode as a literal (single byte)
      encoded.push({
        type: 'literal',
        value: data[position]
      });
      position++;
    }
  }
  
  return encoded;
}

// Decode data using LZ77 algorithm
export function lz77Decode(encodedData) {
  const decoded = [];
  
  for (const item of encodedData) {
    if (item.type === 'literal') {
      decoded.push(item.value);
    } else if (item.type === 'reference') {
      // For references, copy the specified number of bytes from the given offset
      const start = decoded.length - item.offset;
      for (let i = 0; i < item.length; i++) {
        decoded.push(decoded[start + i]);
      }
      // Add the next character
      if (item.nextChar !== undefined) {
        decoded.push(item.nextChar);
      }
    }
  }
  
  return new Uint8Array(decoded);
}

// Helper function to convert LZ77 output to a more compact format
export function compressLZ77Output(encodedData) {
  const result = [];
  
  for (const item of encodedData) {
    if (item.type === 'literal') {
      // For literals, use a single byte with MSB = 0
      result.push(item.value & 0x7F);
    } else {
      // For references, use a 3-byte sequence:
      // Byte 1: MSB=1 (indicates reference), next 7 bits = length
      // Byte 2-3: 16-bit offset (big endian)
      // Byte 4: next character (if any)
      const lengthByte = 0x80 | (item.length & 0x7F);
      result.push(lengthByte);
      result.push((item.offset >> 8) & 0xFF);
      result.push(item.offset & 0xFF);
      if (item.nextChar !== undefined) {
        result.push(item.nextChar);
      }
    }
  }
  
  return new Uint8Array(result);
}

// Helper function to decompress LZ77 input
export function decompressLZ77Input(compressedData) {
  const encoded = [];
  let i = 0;
  
  while (i < compressedData.length) {
    const byte = compressedData[i++];
    
    if ((byte & 0x80) === 0) {
      // Literal
      encoded.push({
        type: 'literal',
        value: byte
      });
    } else {
      // Reference
      const length = byte & 0x7F;
      const offset = (compressedData[i++] << 8) | compressedData[i++];
      const nextChar = i < compressedData.length ? compressedData[i++] : undefined;
      
      encoded.push({
        type: 'reference',
        offset,
        length,
        nextChar
      });
    }
  }
  
  return encoded;
}
